import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Menu,
  RotateCcw,
  ShieldAlert,
  UserRound,
  XCircle,
} from 'lucide-react';
import { answersMatch, examQuestions } from './examQuestions';

type Answers = Record<number, string[]>;
type Stage = 'intro' | 'exam' | 'result';

const answerStorageKey = 'ailan-beauty-exam-answers-v1';
const durationSeconds = 45 * 60;

function loadAnswers(): Answers {
  try {
    const saved = localStorage.getItem(answerStorageKey);
    return saved ? JSON.parse(saved) as Answers : {};
  } catch {
    return {};
  }
}

function formatTime(seconds: number) {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
  const rest = (seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${rest}`;
}

function formatAnswer(question: (typeof examQuestions)[number], selected: string[] | undefined) {
  if (!selected?.length) return '未作答';
  return selected.map((key) => `${key}. ${question.options.find((option) => option.key === key)?.text ?? '未知选项'}`).join('；');
}

const typeLabels = { single: '单选题', multiple: '多选题', true: '判断题', scenario: '情景题' } as const;

export function ExamPage({ onMenuOpen }: { onMenuOpen: () => void }) {
  const [stage, setStage] = useState<Stage>('intro');
  const [employeeName, setEmployeeName] = useState('');
  const [answers, setAnswers] = useState<Answers>(loadAnswers);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(durationSeconds);
  const current = examQuestions[currentIndex]!;
  const answeredCount = examQuestions.filter((question) => (answers[question.id]?.length ?? 0) > 0).length;

  const result = useMemo(() => {
    let score = 0;
    let redLineWrong = 0;
    const wrong = examQuestions.filter((question) => {
      const correct = answersMatch(answers[question.id], question.answer);
      if (correct) score += question.score;
      if (!correct && question.redLine) redLineWrong += 1;
      return !correct;
    });
    return { score, redLineWrong, wrong, passed: score >= 80 && redLineWrong < 3 };
  }, [answers]);

  useEffect(() => {
    if (stage !== 'exam') return;
    const timer = window.setInterval(() => {
      setSecondsLeft((currentSeconds) => {
        if (currentSeconds <= 1) {
          window.clearInterval(timer);
          setStage('result');
          return 0;
        }
        return currentSeconds - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [stage]);

  useEffect(() => {
    localStorage.setItem(answerStorageKey, JSON.stringify(answers));
  }, [answers]);

  const choose = (key: string) => {
    setAnswers((previous) => {
      const selected = previous[current.id] ?? [];
      const next = current.type === 'multiple'
        ? selected.includes(key) ? selected.filter((value) => value !== key) : [...selected, key]
        : [key];
      return { ...previous, [current.id]: next };
    });
  };

  const submit = () => {
    const unanswered = examQuestions.length - answeredCount;
    if (unanswered > 0 && !window.confirm(`还有 ${unanswered} 道题未作答，确定交卷吗？`)) return;
    setStage('result');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const restart = () => {
    localStorage.removeItem(answerStorageKey);
    setAnswers({});
    setCurrentIndex(0);
    setSecondsLeft(durationSeconds);
    setStage('intro');
  };

  return (
    <main className="main examMain">
      <header className="topbar examTopbar">
        <button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button>
        <strong>新员工考试</strong>
        <span>护肤美妆与平台规则</span>
        {stage === 'exam' && <div className={`examTimer ${secondsLeft < 300 ? 'urgent' : ''}`}><Clock3 />剩余 {formatTime(secondsLeft)}</div>}
      </header>

      {stage === 'intro' && (
        <div className="examIntro">
          <section className="examIntroHead">
            <span className="examIntroIcon"><ClipboardCheck /></span>
            <div><p>岗前能力测试</p><h1>护肤美妆客服基础考试</h1><span>检验商品知识、服务判断和平台红线意识</span></div>
          </section>
          <section className="examBrief">
            <div><strong>30</strong><span>题目总数</span></div>
            <div><strong>100</strong><span>试卷满分</span></div>
            <div><strong>80</strong><span>及格分数</span></div>
            <div><strong>45</strong><span>考试分钟</span></div>
          </section>
          <section className="examRules">
            <h2>考试说明</h2>
            <div><CheckCircle2 /><span><strong>题型组成</strong><small>18道单选、5道多选、4道判断、3道情景处理题</small></span></div>
            <div><ShieldAlert /><span><strong>红线要求</strong><small>总分达到80分，且平台红线题答错少于3道，才算通过</small></span></div>
            <div><Clock3 /><span><strong>自动保存</strong><small>作答进度保存在当前设备，45分钟结束后自动交卷</small></span></div>
          </section>
          <label className="employeeField"><span>员工姓名</span><div><UserRound /><input value={employeeName} onChange={(event) => setEmployeeName(event.target.value)} placeholder="请输入姓名后开始考试" /></div></label>
          <button className="button primary examStart" disabled={!employeeName.trim()} onClick={() => setStage('exam')}>开始考试<ArrowRight /></button>
          {answeredCount > 0 && <button className="resumeExam" onClick={() => setStage('exam')}>检测到未完成的答题记录，继续上次考试</button>}
        </div>
      )}

      {stage === 'exam' && (
        <div className="examContent">
          <section className="examProgressBand">
            <div><strong>{employeeName || '未填写姓名'}</strong><span>已完成 {answeredCount} / {examQuestions.length}</span></div>
            <div className="examProgress"><span style={{ width: `${answeredCount / examQuestions.length * 100}%` }} /></div>
            <button className="button" onClick={submit}><ClipboardCheck />交卷</button>
          </section>
          <div className="examWorkspace">
            <aside className="examNavigator">
              <header><strong>答题卡</strong><span>点击题号快速跳转</span></header>
              <div className="questionNumbers">
                {examQuestions.map((question, index) => <button key={question.id} className={`${index === currentIndex ? 'current' : ''} ${answers[question.id]?.length ? 'answered' : ''}`} onClick={() => setCurrentIndex(index)} aria-label={`第${question.id}题`}>{question.id}</button>)}
              </div>
              <footer><span><i className="answered" />已作答</span><span><i className="current" />当前题</span></footer>
            </aside>
            <section className="examQuestionPanel">
              <header>
                <div><span>{typeLabels[current.type]}</span><span>{current.category}</span>{current.redLine && <em><ShieldAlert />红线题</em>}</div>
                <strong>{current.score} 分</strong>
              </header>
              <div className="examQuestionBody">
                <p className="questionPosition">第 {current.id} 题 / 共 {examQuestions.length} 题</p>
                <h1>{current.title}</h1>
                {current.type === 'multiple' && <p className="multiHint">本题有多个正确答案，请选择全部正确选项</p>}
                <div className="examOptions">
                  {current.options.map((option) => {
                    const selected = answers[current.id]?.includes(option.key) ?? false;
                    return <label key={option.key} className={selected ? 'selected' : ''}>
                      <input type={current.type === 'multiple' ? 'checkbox' : 'radio'} name={`question-${current.id}`} checked={selected} onChange={() => choose(option.key)} />
                      <span className="optionKey">{selected ? <Check /> : option.key}</span><span>{option.text}</span>
                    </label>;
                  })}
                </div>
              </div>
              <footer>
                <button className="button" disabled={currentIndex === 0} onClick={() => setCurrentIndex((index) => index - 1)}><ArrowLeft />上一题</button>
                {currentIndex < examQuestions.length - 1
                  ? <button className="button primary" onClick={() => setCurrentIndex((index) => index + 1)}>下一题<ArrowRight /></button>
                  : <button className="button primary" onClick={submit}><ClipboardCheck />完成并交卷</button>}
              </footer>
            </section>
          </div>
        </div>
      )}

      {stage === 'result' && (
        <div className="examResult">
          <section className={`resultSummary ${result.passed ? 'passed' : 'failed'}`}>
            <span className="resultIcon">{result.passed ? <CheckCircle2 /> : <XCircle />}</span>
            <div><p>{result.passed ? '考试通过' : '考试未通过'}</p><h1>{result.score}<small> / 100分</small></h1><span>{employeeName || '本次考试'} · 答对 {examQuestions.length - result.wrong.length} 题 · 红线错题 {result.redLineWrong} 道</span></div>
            <button className="button" onClick={restart}><RotateCcw />重新考试</button>
          </section>
          {!result.passed && <div className="resultNotice"><AlertTriangle /><span>{result.score < 80 ? '总分未达到80分。' : ''}{result.redLineWrong >= 3 ? `红线题答错${result.redLineWrong}道，需重新培训。` : ''}</span></div>}
          <section className="wrongReview">
            <header><div><h2>{result.wrong.length ? '错题解析' : '全部答对'}</h2><p>{result.wrong.length ? '请重点复习以下知识点后再进行考试' : '商品知识和平台规则掌握良好'}</p></div><span>{result.wrong.length} 道错题</span></header>
            {result.wrong.map((question) => <article key={question.id}>
              <div className="wrongMeta"><span>第 {question.id} 题 · {typeLabels[question.type]}</span>{question.redLine && <em><ShieldAlert />红线题</em>}<b>{question.score} 分</b></div>
              <h3>{question.title}</h3>
              <p><span>你的答案</span>{formatAnswer(question, answers[question.id])}</p>
              <p className="correctAnswer"><span>正确答案</span>{formatAnswer(question, question.answer)}</p>
              <div className="answerExplanation"><CheckCircle2 />{question.explanation}</div>
            </article>)}
          </section>
        </div>
      )}
    </main>
  );
}
